import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Home, MapPin, Wallet, Ruler, Plus, GitCompareArrows } from "lucide-react";

const money = (v, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: (cur || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(v || 0);

export default function ComparePage() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(Array.isArray(data) ? data : (data.projects || []));
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 3) return s; // max 3
      return [...s, id];
    });
  };

  const cards = useMemo(() => selected.map((id) => projects.find((p) => p.id === id)).filter(Boolean), [selected, projects]);

  const rows = [
    { label: "Country",       get: (p) => `${p.city ? p.city + ", " : ""}${p.country}` },
    { label: "Type",          get: (p) => p.type },
    { label: "Price",         get: (p) => money(p.price_from, p.currency) },
    { label: "Status",        get: (p) => p.status },
    { label: "Availability",  get: (p) => `${p.units_available} / ${p.units_total} units` },
    { label: "Bedrooms",      get: (p) => p.bedrooms || "—" },
    { label: "Handover",      get: (p) => p.handover || "—" },
    { label: "Local reserved",get: (p) => `${p.local_reservation_pct ?? 40}%` },
  ];

  return (
    <main className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20" data-testid="compare-page">
      <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Side-by-Side</span></div>
      <h1 className="font-display text-4xl md:text-5xl mv-green-text mt-3">
        Compare projects — <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>up to 3.</span>
      </h1>
      <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--mv-muted)" }}>
        Pick projects from the list below. Same clear fields for each — no marketing spin.
      </p>

      {/* Selection */}
      <div className="mt-8">
        <div className="text-[11px] uppercase tracking-[0.2em] mb-3" style={{ color: "var(--mv-muted)" }}>
          {selected.length}/3 selected
        </div>
        {loading ? <div className="text-neutral-500">Loading projects…</div> :
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {projects.map((p) => {
              const isOn = selected.includes(p.id);
              const disabled = !isOn && selected.length >= 3;
              return (
                <button
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  disabled={disabled}
                  className={`text-left mv-card p-4 border transition-colors ${isOn ? "!border-[color:var(--mv-gold)] ring-2 ring-[color:var(--mv-gold)]/40" : ""} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`compare-toggle-${p.id}`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="font-display text-lg mv-green-text leading-tight">{p.name}</div>
                    {isOn ? <X size={14}/> : <Plus size={14}/>}
                  </div>
                  <div className="text-[12.5px] mt-1" style={{ color: "var(--mv-muted)" }}>{p.city ? p.city+", " : ""}{p.country}</div>
                  <div className="mt-2 font-semibold mv-green-text">{money(p.price_from, p.currency)}</div>
                </button>
              );
            })}
          </div>
        }
      </div>

      {/* Comparison table */}
      {cards.length > 0 && (
        <div className="mt-10 mv-card overflow-x-auto" data-testid="compare-table">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b mv-hairline">
                <th className="p-4 text-left text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Field</th>
                {cards.map((p) => (
                  <th key={p.id} className="p-4 text-left">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-display text-lg mv-green-text leading-tight">{p.name}</div>
                        <Link to={`/projects/${p.slug || p.id}`} className="text-[11.5px]" style={{ color: "var(--mv-gold)" }}>View project →</Link>
                      </div>
                      <button onClick={() => toggle(p.id)} className="p-1 hover:bg-black/5 rounded-full" aria-label="Remove"><X size={14}/></button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b mv-hairline last:border-0">
                  <td className="p-4 text-[12px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{row.label}</td>
                  {cards.map((p) => <td key={p.id} className="p-4 mv-green-text font-semibold">{row.get(p)}</td>)}
                </tr>
              ))}
              <tr>
                <td className="p-4 text-[12px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Description</td>
                {cards.map((p) => <td key={p.id} className="p-4 text-[13px]" style={{ color: "var(--mv-text)" }}>{p.description || "—"}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
